import {init} from '@fullstory/browser'

import {waitForConsentPreferences} from '@github-ui/cookie-consent'
import {doNotTrack} from '@github-ui/do-not-track'
import {sendEvent} from '@github-ui/hydro-analytics'
import {attr, controller} from '@github/catalyst'

const FULL_STORY_ORG_ID = 'o-1FH3DA-na1'
const NUMBER_OF_VARIANTS = 4 // 25% sampling rate

/**
 * Buckets a user into a deterministic variant using a simple hash function.
 * Returns a number between 0 and n-1.
 *
 * @param id - The user ID to bucket.
 * @param salt - A string to salt the hash.
 * @param n - The number of variants. (e.g. 2 for a 50% split, 3 for a 33% split, etc.)
 * @returns number The variant number (0 to n-1)
 */
const variantBucket = (id: string, salt: string, n: number): number => {
  const input = `${id}:${salt}`

  // Build a hash
  let hash = 0
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash >>>= 0 // Ensure unsigned 32-bit
  }

  // Take absolute value to ensure positive number, then modulo to get bucket 0 to n-1
  return Math.abs(hash) % n
}

@controller('fullstory-capture')
export class FullstoryCaptureElement extends HTMLElement {
  @attr fsScriptDomain = ''

  async connectedCallback() {
    if (doNotTrack()) {
      return
    }

    // Check if FullStory sampling is enabled via cookie
    if (!this.isUserSampled(NUMBER_OF_VARIANTS)) {
      return
    }

    const consent = await waitForConsentPreferences()
    if (consent?.['Analytics']) {
      const fsScriptUrl = new URL('./fs.js', import.meta.url).toString().split('//')[1]
      init({
        orgId: FULL_STORY_ORG_ID,
        script: fsScriptUrl,
      })
    }
  }

  isUserSampled(nVariants: number): boolean {
    // Use a regular expression to extract the _octo cookie value
    const match = document.cookie.match(/(?:^|;\s*)_octo=([a-zA-Z0-9_.-]+)/)
    const octoCookie = match?.[1]

    if (!octoCookie) {
      sendEvent('fullstory.sampling', {sampled: false, reason: 'no _octo cookie'})

      // If the octo cookie is not set, don't sample.
      return false
    }

    const bucket = variantBucket(octoCookie, 'fs_sampling', nVariants)

    const isSampled = bucket === 0 // Only bucket 0 is sampled (25% if nVariants is 4)

    sendEvent('fullstory.sampling', {sampled: isSampled, reason: `bucket ${bucket} of ${nVariants} variants`})

    return isSampled
  }
}
